import { ForwardRefExoticComponent, RefAttributes } from "react";
import { Company } from "./redux/features/companyProfileSlice";
import { LucideProps } from "lucide-react";

const userTypeList = ["Admin", "Candidate", "Company"] as const;
export type UserType = (typeof userTypeList)[number];

/* Candidate */
export const candidateWaitingRoomStatusList = [
  "New",
  "Invited to Apply",
  "Application Under Review",
  "Interviewing",
  "Interview Under Review",
  "Ready For MAP",
  "Taking/Sent MAP",
  "MAP Under Review",
  "Joined",
  "Dropped out",
  "Rejected",
] as const;
export type CandidateWaitingRoomStatus =
  (typeof candidateWaitingRoomStatusList)[number];

/* Job Opening */
export interface JobOpening {
  id: string;
  role?: string;
  type: OpeningType;
  jobDescriptionLink?: string;
  shortDescription?: string;
  deliverables?: string;
  goals?: string;
  scopeEngagementDuration?: string;
  scopeTime?: string;
  budgetType?: BudgetType;
  budgetMaxCents?: number;
  budgetMinCents?: number;
  budgetNotes?: string;
  desiredStartDate?: string;
  matchCriteria1?: string;
  matchCriteria2?: string;
  matchCriteria3?: string;
  status?: JobOpeningStatus;
}

export interface CompanyJobOpening extends JobOpening {
  companyRecordId: string;
  noteForVenturous?: string;
  candidates?: CandidateStatus[];
  contracts: Contract[];
}

export interface CandidateJobOpening extends JobOpening {
  candidateStatus?: MinimalCandidateStatus;
  company?: Company;
  contracts?: Contract[];
  createdAt: string;
}

export interface DraftJobOpeningDescription {
  id: string;
  jobOpeningId: string;
  description: string;
  createdAt: string;
}

export const openingTypeList = ["Full-time", "Fractional", "Project"] as const;
export type OpeningType = (typeof openingTypeList)[number];

export const budgetTypeList = [
  "Fixed project budget",
  "Monthly retainer",
  "Yearly salary",
] as const;
export type BudgetType = (typeof budgetTypeList)[number];

export const jobOpeningStatusList = [
  "Draft",
  "New Opening",
  "Creating Job Spec",
  "Identifying Candidates",
  "Presenting Shortlist",
  "Role Filled (won)",
  "Role Filled (lost)",
] as const;
export type JobOpeningStatus = (typeof jobOpeningStatusList)[number];

export const checklistSourcingStatusesList = [
  "N/A",
  "Not started",
  "In progress",
  "Done",
] as const;
export type ChecklistSourcingStatuses =
  (typeof checklistSourcingStatusesList)[number];

export const checklistContractStatusesList = [
  "N/A",
  "Not started",
  "In progress",
  "Signed",
] as const;
export type ChecklistContractStatuses =
  (typeof checklistContractStatusesList)[number];

/* Candidate Status */
export interface CandidateStatus {
  id: string;
  userId: string;
  candidateId: string;
  name?: string;
  email: string;
  headline?: string;
  profilePictureLink?: string;
  location?: string;
  status?: CandidateApplicationStatus;
  scope?: string;
  agreesWithScope?: boolean;
  budgetType?: BudgetType;
  budgetHourly?: string;
  budgetMonthly?: string;
  agreesWithPay?: boolean;
  earliestStartDate?: string;
  agreesWithStartDate?: boolean;
  interviews: CompanyInterview[];
  offers: Offer[];
  billing?: Billing;
  payments: Payment[];
}

export interface MinimalCandidateStatus {
  id: string;
  jobOpeningId: string;
  status?: CandidateApplicationStatus;
  scope?: string;
  agreesWithScope?: boolean;
  budgetType?: BudgetType;
  budgetHourly?: string;
  budgetMonthly?: string;
  budgetMinCents?: number;
  budgetMaxCents?: number;
  agreesWithPay?: boolean;
  earliestStartDate?: string;
  agreesWithStartDate?: boolean;
  matchCriteria1?: string;
  matchCriteria1Quote?: string;
  matchCriteria2?: string;
  matchCriteria2Quote?: string;
  matchCriteria3?: string;
  matchCriteria3Quote?: string;
  interviews: CandidateInterview[];
  offers: Offer[];
  billing?: Billing;
  payments: Payment[];
}

export const candidateOpportunityMonthlyRetainerList = [
  "$1,000 - $3,000",
  "$3,001 - $5,000",
  "$5,001 - $7,000",
  "$7,001 - $9,000",
  "$9,001 - $11,000",
  "$11,001 - $13,000",
  "$13,001 - $15,000",
  "$15,001 - $20,000",
  "$20,001 - $25,000",
  "$25,001 - $30,000",
  "$30,001+",
];
export type CandidateOpportunityMonthlyRetainer =
  (typeof candidateOpportunityMonthlyRetainerList)[number];

export const candidateOpportunityHourlyRateList = [
  "$25 to $100",
  "$101 to $200",
  "$201 to $300",
  "$301 to $400",
  "$401 to $500",
  "$501+",
] as const;
export type CandidateOpportunityHourlyRateList =
  (typeof candidateOpportunityHourlyRateList)[number];

export interface CandidateAcceptOpportunityData {
  agreesWithScope: boolean;
  agreesWithPay: boolean;
  agreesWithStartDate: boolean;
  scope?: string;
  budgetType?: BudgetType;
  budgetHourly?: string;
  budgetMonthly?: string;
  budgetMinCents?: number;
  budgetMaxCents?: number;
  earliestStartDate?: string;
  matchCriteria1Quote?: string;
  matchCriteria2Quote?: string;
  matchCriteria3Quote?: string;
  matchCriteria1Suggestion?: string;
  matchCriteria2Suggestion?: string;
  matchCriteria3Suggestion?: string;
}

export interface CandidateDeclineOpportunityData {
  declineReasons: CandidateDeclineReason[];
  otherDeclineReason?: string;
}

export const candidateApplicationStatusList = [
  "Identified",
  "Initial Outreach",
  "Checking Interest",
  "Screening",
  "Possible Fit",
  "Shortlisted",
  "Making Offer",
  "Contracting",
  "Accepted by Client",
  "Rejected for Role",
  "Not Interested",
] as const;
export type CandidateApplicationStatus =
  (typeof candidateApplicationStatusList)[number];

export const candidateDeclineReasonList = [
  "Compensation",
  "Requirements or skill mismatch",
  "Too many hours",
  "Too few hours",
  "Start date doesn't align",
  "Lack of industry fit",
  "Lack of availability",
] as const;
export type CandidateDeclineReason =
  (typeof candidateDeclineReasonList)[number];

/* Interview */
export interface CreateInterview {
  name: string;
  duration: number;
  jobOpeningId: string;
  candidateId: string;
  participants: string[];
}

export type UpdateInterview = Omit<
  CreateInterview,
  "jobOpeningId" | "candidateId"
>;

export interface CompanyInterview {
  id: string;
  name: string;
  duration: number;
  participants: string[];
  scheduled: boolean;
  startTime?: string;
  link?: string;
  isCompleted?: boolean;
}

export type CandidateInterview = Omit<CompanyInterview, "participants">;

export const weekdayList = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
export type Weekday = (typeof weekdayList)[number];

export interface CandidateAvailability {
  id: string;
  interview: string;
  startTime: string;
  endTime: string;
  timezone: string;
}

export interface GeneralCandidateAvailability extends CandidateAvailability {
  weekday: Weekday;
}
export interface NewGeneralCandidateAvailability
  extends Omit<GeneralCandidateAvailability, "id"> {
  user: string;
}
export type GeneralCandidateAvailabilityUpdate = Omit<
  GeneralCandidateAvailability,
  "id"
>;

export interface SpecificCandidateAvailability extends CandidateAvailability {
  dates: string[];
}
export interface NewSpecificCandidateAvailability
  extends Omit<SpecificCandidateAvailability, "id"> {
  user: string;
}
export type SpecificCandidateAvailabilityUpdate = Omit<
  SpecificCandidateAvailability,
  "id"
>;

/* Offer */
export const offerStatusList = [
  "sent",
  "accepted",
  "rejected",
  "superseded",
  "withdrawn",
] as const;
export type OfferStatus = (typeof offerStatusList)[number];

export const offerSenderList = ["company", "candidate"] as const;
export type OfferSender = (typeof offerSenderList)[number];

export interface OfferTermFullTime {
  termType: "full-time";
  startDate: string;
  totalAmountCents: number;
}

export interface OfferTermMilestones {
  termType: "milestone";
  milestoneLabel: string;
  totalAmountCents: number;
}

export type OfferTermRecurring =
  | {
      termType: "recurring";
      iterations: number;
      isOpenEnded?: false;
      billingDay: number;
      totalAmountCents: number;
    }
  | {
      termType: "recurring";
      iterations?: null;
      isOpenEnded: true;
      billingDay: number;
      totalAmountCents: number;
    };

export type NewOfferTerm =
  | OfferTermFullTime
  | OfferTermMilestones
  | OfferTermRecurring;

export type OfferTerm = NewOfferTerm & {
  platformFeePercentage?: number;
};

export interface NewOffer {
  terms: NewOfferTerm[];
  note?: string;
}

export interface Offer {
  id: string;
  terms: OfferTerm[];
  status: OfferStatus;
  sender: OfferSender;
  note?: string;
  createdAt: string;
}

/* Billing */
export const milestoneStatusList = [
  "in_progress",
  "submitted",
  "disputed",
  "paid",
] as const;
export type MilestoneStatus = (typeof milestoneStatusList)[number];

export interface NewBillingTermFullTime {
  termType: "full-time";
  totalAmountCents: number;
}

export interface NewBillingTermMilestone {
  termType: "milestone";
  milestoneLabel: string;
  totalAmountCents: number;
}

export type NewBillingTermRecurring =
  | {
      termType: "recurring";
      iterations: number;
      isOpenEnded?: false;
      billingDay: number;
      totalAmountCents: number;
    }
  | {
      termType: "recurring";
      iterations?: null;
      isOpenEnded: true;
      billingDay: number;
      totalAmountCents: number;
    };

export type NewBillingTerm = (
  | NewBillingTermFullTime
  | NewBillingTermMilestone
  | NewBillingTermRecurring
) & {
  candidateAmountCents: number;
  platformFeeCents: number;
};

export interface NewBillingPlan {
  firstBillingDate: string; // YYYY-MM-DD
  terms: NewBillingTerm[];
}

export type BaseBillingTermFullTime = NewBillingTermFullTime;

export interface BaseBillingTermMilestone extends NewBillingTermMilestone {
  status: MilestoneStatus;
  submittedAt?: string;
}

export type BaseBillingTermRecurring = NewBillingTermRecurring;

export type BillingPlanTerm = (
  | BaseBillingTermFullTime
  | BaseBillingTermMilestone
  | BaseBillingTermRecurring
) & {
  id: string;
  currency: string;
  candidateAmountCents: number;
  platformFeeCents: number;
};

export interface BillingPlan {
  firstBillingDate: string; // ISO
  terms: BillingPlanTerm[];
}

const companyCheckoutStatusList = [
  "not_started",
  "pending_checkout",
  "active",
  "completed",
  "cancelled",
  "failed",
] as const;
export type CompanyCheckoutStatus = (typeof companyCheckoutStatusList)[number];

export interface Billing extends BillingPlan {
  id: string;
  candidateReady: boolean;
  companyCheckoutStatus: CompanyCheckoutStatus;
}

const paymentStatusList = [
  "paid",
  "void",
  "uncollectible",
  "refunded",
  "needs_review",
  "unknown",
] as const;
export type PaymentStatus = (typeof paymentStatusList)[number];

const payoutStatusList = ["pending", "succeeded", "failed"];
export type PayoutStatus = (typeof payoutStatusList)[number];

/* Payments */
export interface Payment {
  id: string;
  status: PaymentStatus;
  currency: string;
  paidAt?: string;
  totalAmountCents: number;
  candidateAmountCents: number;
  platformFeeCents: number;
  taxCents: number;
  billingPlanTermId?: string;
  payout?: {
    id: string;
    status: PayoutStatus;
    amountCents: number;
    lastAttemptAt?: string;
    failureReason?: string;
  };
}

/* Contract */
export interface Contract {
  id: string;
  name: string;
  link: string;
}

/* Resource */
export interface Resource {
  id: string;
  headline: string;
  summary: string;
  image: string;
  link: string;
}

export const resourceAudienceList = ["Company", "Candidate", "VC"] as const;
export type ResourceAudience = (typeof resourceAudienceList)[number];

/* Admin */
export interface AdminPayment {
  id: string;
  candidateAmountCents: number;
  venturousAmountCents: number;
  taxAmountCents: number;
  totalAmountCents: number;
  company: {
    id: string;
    name?: string;
    logoLink?: string;
  };
  user: {
    id: string;
    name?: string;
    profilePictureLink?: string;
  };
  jobOpening: {
    id: string;
    role?: string;
    openingType: OpeningType;
  };
  status: PaymentStatus;
  createdAt: string;
  paidAt?: string;
}

export interface AdminPayout {
  id: string;
  totalAmountCents: number;
  user: {
    id: string;
    name?: string;
    profilePictureLink?: string;
  };
  company: {
    id: string;
    name?: string;
    logoLink?: string;
  };
  jobOpening: {
    id: string;
    role?: string;
    openingType?: OpeningType;
  };
  payoutAttempts: {
    id: string;
    attemptNumber: number;
    status: PayoutStatus;
    errorMessage?: string;
  }[];
  status: PayoutStatus;
  createdAt: string;
  lastAttemptAt?: string;
}

/* SVG */
export type SvgJsxType = (props: {
  className?: string;
  height?: string;
  width?: string;
  fill?: string;
  stroke?: string;
  onClick?: () => void;
}) => JSX.Element;

/* Error Payload */
export interface ErrorPayload {
  error: ErrorType;
  message: string;
}

export type ErrorType =
  | "UNEXPECTED"
  | "UNAUTHORIZED"
  | "TOKEN_ERROR"
  | "SERVER_ERROR"
  | "GENERIC_BAD_INPUT"
  | "SPECIFIC_BAD_INPUT"
  | "MISSED_CHECKOUT_WINDOW"
  | "INVALID_ACTION"
  | "NOT_FOUND"
  | "PARTIAL_SUCCESS";

export type IconProp = ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
>;
